import { useMemo } from 'react';
import { Spin, Empty } from 'antd';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { usePlayerOptional } from '../usePlayerContext';
import { useStationObservations } from '../useStationObservations';
import { StationCharts } from '../../../components/StationCharts/StationCharts';
import type { WidgetProps } from './types';

export const StationChartsWidget = ({
  config,
  pixelSize,
}: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const player = usePlayerOptional();
  const { stations: allStations, loading, error } = useStationObservations();

  const selectedDateRange = player?.selectedDateRange ?? null;
  const selectedStation = (config.selectedStation as string | undefined) ?? undefined;
  const visibleCharts = (config.visibleCharts as string[] | undefined) ?? undefined;
  const hideLegend = (config.hideLegend as boolean | undefined) ?? false;

  const chartHeight = useMemo(() => {
    const h = pixelSize.height || 400;
    // border(2) + flex-padding(24) + chart-container-padding(8) + chart-title(24) + buffer(4)
    const base = 62;
    // chart-stats blocks when legend is visible
    const legend = hideLegend ? 0 : 44;
    return Math.max(80, h - base - legend);
  }, [pixelSize.height, hideLegend]);

  const station = useMemo(() => {
    if (allStations.length === 0) return null;
    if (selectedStation) {
      const match = allStations.find((s) => s.station_reference === selectedStation);
      if (match) return match;
    }
    return allStations[0];
  }, [allStations, selectedStation]);

  const stationMeta = useMemo(() => {
    if (!station || !selectedEvent?.stations) return undefined;
    return selectedEvent.stations.find(
      (s) => s.reference === station.station_reference,
    );
  }, [station, selectedEvent?.stations]);

  const sessions = useMemo(
    () => selectedEvent?.sessions || [],
    [selectedEvent?.sessions],
  );

  if (loading && allStations.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>No event selected</span>}
        />
      </div>
    );
  }

  if (!selectedDateRange) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>Select a time range on the timeline</span>}
        />
      </div>
    );
  }

  if (error || !station) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontSize: 11 }}>
              {error || 'No station data available'}
            </span>
          }
        />
      </div>
    );
  }

  return (
    <StationCharts
      station={station}
      seasonYear={selectedEvent.season_year}
      circuitId={selectedEvent.circuit_id}
      geojsonId={selectedEvent.circuit_geojson_id}
      selectedStartDate={selectedDateRange.start}
      selectedEndDate={selectedDateRange.end}
      circuitTimeZone={selectedEvent.circuit?.time_zone}
      stationLat={stationMeta?.lat}
      stationLon={stationMeta?.lon}
      sessions={sessions}
        chartHeight={chartHeight}
        hideLegend={hideLegend}
        disableAnimations
        visibleCharts={visibleCharts as Parameters<typeof StationCharts>[0]['visibleCharts']}
    />
  );
};
